/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

export interface SignDialogData {
  signer: string;
  /** Unsaved edits in the studio, which are saved before signing. */
  dirty: boolean;
  /** Signatures already in the file — a visible mark would rewrite it and break them. */
  existing: number;
}

export interface SignDialogResult {
  reason: string;
  location: string;
  /** Place a visible mark on a page first (an extra click on the page). */
  visible: boolean;
  majorVersion: boolean;
}

/** The common reasons, one click away; anything else can be typed. */
export const SIGN_REASONS = ['DMS.PDFTOOLS.DS_R_APPROVE', 'DMS.PDFTOOLS.DS_R_REVIEWED', 'DMS.PDFTOOLS.DS_R_ACK', 'DMS.PDFTOOLS.DS_R_AUTHOR'];

/** Confirms a digital signature: why, where, whether a mark goes on the page, and which version it becomes. */
@Component({
  selector: 'app-sign-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  template: `
    <h2 mat-dialog-title>{{ 'DMS.PDFTOOLS.DS_TITLE' | translate }}</h2>
    <mat-dialog-content class="aca-signdlg">
      <p class="aca-signdlg__who">
        <mat-icon>verified_user</mat-icon>
        <span>{{ 'DMS.PDFTOOLS.DS_AS' | translate: { p0: data.signer } }}</span>
      </p>

      <div class="aca-signdlg__reasons">
        <button
          type="button"
          *ngFor="let r of reasons"
          class="aca-signdlg__reason"
          [class.aca-signdlg__reason--on]="reason === (r | translate)"
          (click)="pickReason(r)"
        >
          {{ r | translate }}
        </button>
      </div>
      <mat-form-field appearance="outline">
        <mat-label>{{ 'DMS.PDFTOOLS.DS_REASON' | translate }}</mat-label>
        <input matInput [(ngModel)]="reason" maxlength="200" cdkFocusInitial />
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>{{ 'DMS.PDFTOOLS.DS_LOCATION' | translate }}</mat-label>
        <input matInput [(ngModel)]="location" maxlength="200" />
      </mat-form-field>

      <mat-checkbox [(ngModel)]="visible" [disabled]="data.existing > 0">{{ 'DMS.PDFTOOLS.DS_VISIBLE' | translate }}</mat-checkbox>
      <p class="aca-signdlg__note" *ngIf="data.existing > 0">{{ 'DMS.PDFTOOLS.DS_VISIBLE_OFF' | translate }}</p>

      <mat-radio-group [(ngModel)]="majorVersion" class="aca-signdlg__version">
        <mat-radio-button [value]="false">{{ 'DMS.PDFTOOLS.SAVE_MINOR' | translate }}</mat-radio-button>
        <mat-radio-button [value]="true">{{ 'DMS.PDFTOOLS.SAVE_MAJOR' | translate }}</mat-radio-button>
      </mat-radio-group>

      <p class="aca-signdlg__note" *ngIf="data.dirty">{{ 'DMS.PDFTOOLS.DS_SAVES_EDITS' | translate }}</p>
      <p class="aca-signdlg__note">{{ 'DMS.PDFTOOLS.DS_NOTE' | translate }}</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="close()">{{ 'DMS.WF_DIALOG.CANCEL' | translate }}</button>
      <button mat-flat-button color="primary" type="button" (click)="confirm()">
        <mat-icon>draw</mat-icon>{{ (visible ? 'DMS.PDFTOOLS.DS_PLACE' : 'DMS.PDFTOOLS.DS_SIGN') | translate }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .aca-signdlg {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: min(460px, 80vw);
      }
      .aca-signdlg__who {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 4px;
        font-weight: 600;
      }
      .aca-signdlg__who .mat-icon {
        color: var(--aca-color-primary);
      }
      .aca-signdlg__reasons {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }
      .aca-signdlg__reason {
        padding: 4px 12px;
        border: 1px solid var(--aca-hairline);
        border-radius: var(--aca-radius-pill);
        background: var(--aca-surface);
        font: inherit;
        font-size: 12.5px;
        cursor: pointer;
      }
      .aca-signdlg__reason--on {
        border-color: var(--aca-color-primary);
        background: var(--aca-color-primary-soft);
        color: var(--aca-color-primary);
      }
      .aca-signdlg__version {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
      }
      .aca-signdlg__note {
        margin: 0;
        font-size: 12px;
        line-height: 1.6;
        color: var(--aca-text-secondary);
      }
    `
  ]
})
export class SignDialogComponent {
  readonly data = inject<SignDialogData>(MAT_DIALOG_DATA);
  private readonly ref = inject(MatDialogRef<SignDialogComponent, SignDialogResult>);
  private readonly translate = inject(TranslateService);

  readonly reasons = SIGN_REASONS;
  reason = '';
  location = '';
  // a mark on the page means rewriting the file, which an earlier signature does not survive
  visible = this.data.existing === 0;
  majorVersion = false;

  pickReason(key: string): void {
    this.reason = this.translate.instant(key);
  }

  confirm(): void {
    this.ref.close({
      reason: this.reason.trim(),
      location: this.location.trim(),
      visible: this.visible && this.data.existing === 0,
      majorVersion: this.majorVersion
    });
  }

  close(): void {
    this.ref.close();
  }
}
